import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
  SimpleGrid,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, CheckIcon, ViewIcon } from "@chakra-ui/icons";
import { FiRefreshCw } from "react-icons/fi";
import { milestoneService, projectService } from "../services/edosService";
import api from "../services/api";

const STATUS_COLORS = {
  pending: "gray",
  in_progress: "blue",
  completed: "teal",
  missed: "red",
  cancelled: "orange",
};

export default function MilestonesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [projectTickets, setProjectTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch (_) {}
  }, []);

  const fetchMilestones = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    try {
      const res = await milestoneService.listByProject(selectedProjectId);
      setMilestones(res.data.data || res.data.milestones || []);
    } catch (err) {
      toast({ title: "Failed to load milestones", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, toast]);

  const normalizeTicketRows = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.tickets)) return payload.data.tickets;
    if (Array.isArray(payload?.tickets)) return payload.tickets;
    return [];
  };

  const fetchProjectTickets = useCallback(async () => {
    if (!selectedProjectId) {
      setProjectTickets([]);
      return;
    }
    setTicketsLoading(true);
    try {
      const allRows = [];
      let page = 1;
      let totalPages = 1;

      do {
        const res = await api.get("/api/tickets", {
          params: {
            page,
            limit: 100,
            projectId: selectedProjectId,
            _viewMode: "all",
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        });

        const payload = res?.data;
        allRows.push(...normalizeTicketRows(payload));
        totalPages = Number(
          payload?.totalPages || payload?.data?.totalPages || 1,
        );
        page += 1;
      } while (page <= totalPages && page <= 50);

      setProjectTickets(allRows);
    } catch {
      setProjectTickets([]);
      toast({ title: "Failed to load milestone tickets", status: "error" });
    } finally {
      setTicketsLoading(false);
    }
  }, [selectedProjectId, toast]);

  const ticketCountsByMilestone = useMemo(() => {
    const counts = {};
    projectTickets.forEach((ticket) => {
      const mid = ticket?.milestoneId?._id || ticket?.milestoneId;
      if (!mid) return;
      counts[mid] = (counts[mid] || 0) + 1;
    });
    return counts;
  }, [projectTickets]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);
  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones]);

  useEffect(() => {
    fetchProjectTickets();
  }, [fetchProjectTickets]);

  const handleViewTickets = (milestone) => {
    navigate(`/edos/milestones/${milestone._id}`);
  };

  const handleComplete = async (id) => {
    try {
      await milestoneService.complete(id);
      toast({ title: "Milestone completed", status: "success" });
      fetchMilestones();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      await milestoneService.remove(id);
      toast({ title: "Milestone deleted", status: "success" });
      fetchMilestones();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Milestones
          </Text>
          <Text color="gray.500" fontSize="sm">
            Track key delivery milestones per project
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={() => {
              fetchMilestones();
              fetchProjectTickets();
            }}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="teal"
            onClick={() =>
              navigate(
                `/edos/milestones/create${selectedProjectId ? `?projectId=${selectedProjectId}` : ""}`,
              )
            }
          >
            New Milestone
          </Button>
        </HStack>
      </Flex>

      <Flex gap={3} mb={4} align="center">
        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
          Select Project:
        </Text>
        <Select
          placeholder="Choose a project..."
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          maxW="320px"
        >
          {projects.map((p) => (
            <option key={p._id} value={p._id}>
              {p.projectName}
            </option>
          ))}
        </Select>
      </Flex>

      {!selectedProjectId ? (
        <Alert status="info">
          <AlertIcon />
          Select a project to view its milestones.
        </Alert>
      ) : (
        <Box
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          overflow="hidden"
        >
          {loading ? (
            <Flex justify="center" p={10}>
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : milestones.length === 0 ? (
            <Alert status="info" m={4}>
              <AlertIcon />
              No milestones yet.
            </Alert>
          ) : (
            <Table variant="simple" size="sm">
              <Thead bg={headerBg}>
                <Tr>
                  <Th>Milestone</Th>
                  <Th>Status</Th>
                  <Th>Due Date</Th>
                  <Th>Overdue</Th>
                  <Th>Tickets</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {milestones.map((m) => (
                  <Tr key={m._id}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {m.milestoneName}
                        </Text>
                        {m.description && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {m.description}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS[m.status] || "gray"}>
                        {m.status?.replace(/_/g, " ")}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {m.dueDate
                          ? new Date(m.dueDate).toLocaleDateString()
                          : "-"}
                      </Text>
                    </Td>
                    <Td>
                      {m.isOverdue ? (
                        <Badge colorScheme="red">Overdue</Badge>
                      ) : (
                        <Badge colorScheme="green">On Track</Badge>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {ticketCountsByMilestone[m._id] || 0}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="View Tickets">
                          <IconButton
                            size="xs"
                            icon={<ViewIcon />}
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleViewTickets(m)}
                          />
                        </Tooltip>
                        {m.status !== "completed" && (
                          <Tooltip label="Mark Complete">
                            <IconButton
                              size="xs"
                              icon={<CheckIcon />}
                              variant="ghost"
                              colorScheme="teal"
                              onClick={() => handleComplete(m._id)}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="Delete">
                          <IconButton
                            size="xs"
                            icon={<DeleteIcon />}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(m._id)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
}
